import React, { Children, ReactElement, useEffect, useState } from 'react';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { QUIZ_ORDER } from '../itemIds';
import styled from '@xstyled/styled-components';
import {
  QuizOrderProvider,
  useQuizOrderStore
} from '~/training/sequence/templates/items/quiz-order/QuizOrderStore';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import Icon from '~/assets/icons/next.svg';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { shallow } from 'zustand/shallow';
import { CheckButton } from './components/CheckButton';
import { ConfirmationPopup } from './components/ConfimationPopup';
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';

interface Props {
  variant?: string;
  correctAnswers: (string | number)[];
}

const FlexBlock = styled.divBox`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: md;
  > button {
    flex: 1;
  }
  > button:focus {
    outline: 0;
  }
  &.list > button {
    margin-left: 0px;
    margin-right: 0px;
    min-width: 100%;
  }
  &.list > .feedback {
    width: 100%;
    margin-top: 10px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const NextIcon = styled(Icon)<{ title: string }>``;

const QuizOrderList = ({ children }: { children: ReactElement[] }) => {
  const { array, setArray } = useQuizOrderStore();

  useEffect(() => {
    setArray(children);
  }, []);

  return <>{array}</>;
};

const mediaSelector = (s: MediaPlayerStore) =>
  select(s, 'reset', 'loadMedia', 'status');

export type QuizOrderDefinition = ItemDefinition<typeof QUIZ_ORDER, Props>;

export class QuizOrder extends Item<typeof QUIZ_ORDER, Props> {
  protected render({
    id,
    children,
    variant,
    correctAnswers
  }: RenderProps<Props>) {
    const { addBlocker, blockers, sendRxdInteractions } = useSequenceStore();
    const { status } = useMediaPlayerStore(mediaSelector, shallow);
    const { editorMode } = useEditorStore();
    const childArray = Children.toArray(children);

    const { array } = useQuizOrderStore();

    const [disableCheckButton, setDisableCheckButton] = useState(true);
    const [isOpened, setIsOpened] = useState(false);

    const globalMedia = useGlobalMediaStore();
    const globalPlayer = globalMedia?.player;

    useEffect(() => {
      if (globalPlayer || status !== 'done') {
        setDisableCheckButton(true);
      } else {
        setDisableCheckButton(false);
      }
      if (blockers.length === 0) {
        setDisableCheckButton(true);
      }
    }, [globalPlayer, status, blockers]);

    useGlobalMedia();

    useEffect(() => {
      childArray.map(c => {
        addBlocker((c as React.ReactElement<{ id?: string }>).props.id!);
        return (c as React.ReactElement<{ id?: string }>).props.id;
      });
    }, []);

    let RXD:
      | { RecordSequencingInteraction?: (...args: unknown[]) => void }
      | undefined;

    try {
      if (window.document.referrer) {
        const preUrl = window?.document?.referrer;
        const domain = preUrl?.split('/')[2];

        if (domain !== 'localhost:8000' && domain !== 'www.easyllama.com') {
          RXD = (window.parent as Window & { RXD?: typeof RXD }).RXD;
        }
      } else {
        RXD = (window.parent as Window & { RXD?: typeof RXD }).RXD;
      }
    } catch (error) {
      console.error('RXD in BTN:', error);
    }

    const openCheckWindow = (value: boolean) => {
      setIsOpened(value);

      if (sendRxdInteractions && RXD) {
        const arraysAreEqual = (
          arr1: (string | number)[],
          arr2: (string | number)[]
        ): boolean => {
          if (arr1.length !== arr2.length) {
            return false;
          }
          return arr1.every((value, index) => value === arr2[index]);
        };

        const userAnswer = array.map(i => i.props.id);

        RXD!.RecordSequencingInteraction!(
          id,
          userAnswer,
          arraysAreEqual(correctAnswers, userAnswer),
          correctAnswers
        );
      }
    };

    return (
      <QuizOrderProvider>
        <FlexBlock className={variant}>
          <QuizOrderList children={childArray as ReactElement[]} />
        </FlexBlock>
        {!disableCheckButton && !editorMode && (
          <CheckButton setIsOpened={openCheckWindow}>
            {t`Check`} <NextIcon title={t`Next step icon`} />
          </CheckButton>
        )}
        {isOpened && <ConfirmationPopup setIsOpened={setIsOpened} />}
      </QuizOrderProvider>
    );
  }
}

registry[QUIZ_ORDER] = QuizOrder;
